import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter, ZAxis, PieChart, Pie, Cell } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { TrendingUp, DollarSign, BarChart2, Target, Sparkles, Filter } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import MetricCard from '../components/MetricCard';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const mockData = {
  esgTrend: [
    { year: '2018', score: 65 },
    { year: '2019', score: 70 },
    { year: '2020', score: 72 },
    { year: '2021', score: 78 },
    { year: '2022', score: 85 },
  ],
  esgVsFinancial: [
    { esgScore: 65, financialPerformance: 100, year: '2018' },
    { esgScore: 70, financialPerformance: 120, year: '2019' },
    { esgScore: 72, financialPerformance: 150, year: '2020' },
    { esgScore: 78, financialPerformance: 200, year: '2021' },
    { esgScore: 85, financialPerformance: 250, year: '2022' },
  ],
  esgBreakdown: [
    { name: 'Environmental', value: 40 },
    { name: 'Social', value: 30 },
    { name: 'Governance', value: 30 },
  ],
};

const Analytics = () => {
  const [timeFrame, setTimeFrame] = useState('5y');

  const filterData = (data, frame) => {
    const years = frame === '1y' ? 1 : frame === '3y' ? 3 : 5;
    return data.slice(-years);
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">ESG Analytics</h1>
        <div className="flex items-center space-x-2">
          <Filter className="h-5 w-5 text-gray-500" />
          <Select value={timeFrame} onValueChange={setTimeFrame}>
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Time Frame" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1y">Last Year</SelectItem>
              <SelectItem value="3y">Last 3 Years</SelectItem>
              <SelectItem value="5y">Last 5 Years</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard title="ESG Score" value="85/100" trend={9} icon={TrendingUp} />
        <MetricCard title="Financial Impact" value="+25%" trend={25} icon={DollarSign} />
        <MetricCard title="Industry Rank" value="#3" trend={2} icon={BarChart2} />
        <MetricCard title="Goal Completion" value="92%" trend={5} icon={Target} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>ESG Score Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={filterData(mockData.esgTrend, timeFrame)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="score" stroke="#8884d8" activeDot={{ r: 8 }} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>ESG Score vs Financial Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <CartesianGrid />
              <XAxis type="number" dataKey="esgScore" name="ESG Score" unit="/100" />
              <YAxis type="number" dataKey="financialPerformance" name="Financial Performance" unit="M" />
              <ZAxis type="category" dataKey="year" name="Year" />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} />
              <Legend />
              <Scatter name="ESG vs Financial" data={filterData(mockData.esgVsFinancial, timeFrame)} fill="#8884d8" />
            </ScatterChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>ESG Score Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={mockData.esgBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {mockData.esgBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Sparkles className="mr-2 h-5 w-5 text-yellow-500" />
              AI-Generated Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc pl-5 space-y-2">
              <li>Your ESG score has shown consistent improvement over the past {timeFrame}, with a notable 20% increase since 2018.</li>
              <li>There's a strong positive correlation between your ESG performance and financial growth, suggesting that sustainability initiatives are driving business value.</li>
              <li>Your environmental score is leading the ESG components, contributing 40% to your overall score. Consider maintaining this strength while focusing on improving social and governance aspects.</li>
              <li>Your current trajectory puts you on track to surpass the industry average ESG score within the next 2 years if maintained.</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;